import React, { useContext, useEffect, useMemo, useState } from "react";

import { createTheme } from "@mui/material/styles";
import { ThemeProvider } from "@mui/system";
import {
  coreDarkTheme,
  coreLightTheme,
  gravityDarkTheme,
  gravityLightTheme,
  miromaDarkTheme,
  miromaLightTheme,
  demoTheme,
} from "./clients";
import {
  bupaTheme
} from "./demos";

import { DemoContext, ThemeContext } from "./ThemeContext";

declare module "@mui/material/styles" {
  interface Palette {
    score: Palette["primary"];
    gradient: string[];
    segments: string[];
    segment1: Palette["primary"];
    segment2: Palette["primary"];
    segment3: Palette["primary"];
    segment4: Palette["primary"];
    segment5: Palette["primary"];
    segment6: Palette["primary"];
  }

  interface PaletteOptions {
    score?: PaletteOptions["primary"];
    gradient?: string[];
    segments?: string[];
    segment1?: PaletteOptions["primary"];
    segment2?: PaletteOptions["primary"];
    segment3?: PaletteOptions["primary"];
    segment4?: PaletteOptions["primary"];
    segment5?: PaletteOptions["primary"];
    segment6?: PaletteOptions["primary"];
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsColorOverrides {
    [key: `segment${number}`]: true;
  }
}

declare module "@mui/material/SvgIcon" {
  interface SvgIconPropsColorOverrides {
    [key: `segment${number}`]: true;
  }
}

const THEME_MAP: Record<string, any> = {
  gravity: { dark: gravityDarkTheme, light: gravityLightTheme },
  miroma: { dark: miromaDarkTheme, light: miromaLightTheme },
  bupa: { dark: bupaTheme, light: bupaTheme },
  core: { dark: coreDarkTheme, light: coreLightTheme },
  demo: { dark: demoTheme, light: demoTheme },
};

export const ThemeProviderWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [darkMode, setDarkMode] = useState(true);

  const client = process.env.REACT_APP_CLIENT || "core";
  const isDemoMode = client === 'demo';

  const [demoClient, setDemoClient] = useState<string | undefined>(() => {
    return isDemoMode ? localStorage.getItem('demo-client') || undefined : undefined;
  });

  useEffect(() => {
    if (demoClient) {
      localStorage.setItem('demo-client', demoClient);
    } else {
      localStorage.removeItem('demo-client');
    }
  }, [demoClient])

  const getActiveConfig = () => {
    if (isDemoMode && demoClient && THEME_MAP[demoClient]) {
      return THEME_MAP[demoClient];
    }
    return THEME_MAP[client] || THEME_MAP.core;
  };

  const configs = getActiveConfig();
  const theme = useMemo(() =>
      createTheme(darkMode ? configs.dark : configs.light),
    [darkMode, configs]
  );

  const toggleDarkMode = () => setDarkMode(!darkMode);

  return (
    <DemoContext.Provider value={{ isDemoMode, demoClient, setDemoClient}}>
      <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
        <ThemeProvider theme={theme}>
          {children}
        </ThemeProvider>
      </ThemeContext.Provider>
    </DemoContext.Provider>
  );
};

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export const useDemo = () => {
  const context = useContext(DemoContext);
  if (!context) {
    throw new Error("useThemeControl must be used within a ThemeControlProvider");
  }
  return context;
}
